import { useState } from 'react';
import { Cards, FormValues } from '../pages/templates/rcs/interfaces/interfaces';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { AlertService } from '../services/alert.service';
import { AssetsServices } from '../services/assets.service';
import Icon from '../components/forms/icon.component';
import useBgPages from '../hooks/use-bg-pages.hook';

export const useFunctionsButtons = () => {
  useBgPages();
  const { t } = useTranslation();
  const [files, setFiles] = useState<any[]>([]);
  const {
    register,
    watch,
    setValue,
    getValues,
    clearErrors,
    formState: { errors }
  } = useFormContext<FormValues>();

  const actions = [
    {
      key: 'redirect',
      icon: <i className="bi bi-globe-americas fw-bolder fs-2 text-dark" />,
      title: 'Esta acción llevará al cliente a abrir un enlace al hacer clic.',
      label: 'Redireccionar'
    },
    {
      key: 'call',
      icon: <i className="bi bi-telephone-fill text-dark fw-bolder fs-2" />,
      title:
        'Esta acción llevará al cliente a realizar una llamada al número ingresado al hacer clic.',
      label: 'Llamar'
    },
    {
      key: 'location',
      icon: <Icon icon="ki-geolocation" size="fs-1" className="fw-bolder" type="dark" />,
      title:
        'Esta acción llevará al cliente a abrir la aplicación de mapas predeterminada en la ubicación ingresada al hacer clic.',
      label: 'Ubicación'
    },
    {
      key: 'calendar',
      icon: <Icon icon="ki-calendar-tick" size="fs-1" className="fw-bolder" type="dark" />,
      title:
        'Esta acción llevará al cliente a registrar un evento es su aplicación de calendario al hacer clic.',
      label: 'Calendario'
    },
    {
      key: 'answer',
      icon: <Icon icon="ki-send" size="fs-1" className="fw-bolder" type="dark" />,
      title: 'Esta acción llevará al cliente a abrir un enlace al hacer clic.',
      label: 'Respuesta'
    }
  ];

  // file actions

  const handleUpdateFiles = (
    files: any[],
    index: number,
    type: string,
    dropZone: string,
    cardIndex: number
  ) => {
    if (!files.length) return;
    const file = files[0].file;
    const fileType = file.type;
    const isImage = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(fileType);
    const isVideo = fileType === 'video/mp4';
    const allowedTypes = [
      'pdf',
      'doc',
      'docx',
      'txt',
      'zip',
      'rar',
      'pptx',
      'xls',
      'xlsx',
      'csv',
      'plain',
      'vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const fileSubtype = fileType.split('/')[1];
    const isFile = allowedTypes.some((type: any) => fileSubtype.includes(type));

    if (dropZone === 'headerImage' || dropZone === 'posterImage') {
      if (file.size > 5 * 1024 * 1024) {
        AlertService.toastInfo('¡Atención!, El tamaño máximo permitido para la imagen es de 5 MB.');
        return;
      }

      if (!isImage) {
        AlertService.toastInfo(
          '¡Atención!, El formato de imagen debe ser .png, .jpg, .jpeg o .gif.'
        );
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.src = objectUrl;
      img.onload = async () => {
        const { width, height } = img;
        const maxWidth = type === 'richCard' || type === 'contenInfo' ? 1440 : 960;
        const maxHeight = 720;

        if (width !== maxWidth || height !== maxHeight) {
          const isGif = fileType === 'image/gif';
          const resolutionMessage = isGif
            ? 'La animación del GIF se perderá al redimensionar al tamaño correcto.'
            : '';

          const confirmed = await AlertService.question(
            `¿Deseas redimensionar tu imagen?`,
            `La resolución correcta es ${maxWidth} x ${maxHeight} px. ${resolutionMessage}`,
            null as any
          );

          if (!confirmed) {
            return;
          }
          const resizedBlob = await handleResizeImage(file, maxWidth, maxHeight);
          files[0].file = new File([resizedBlob], file?.name, { type: fileType });
          AlertService.toastSuccess('¡Éxito!, imagen redimensionada correctamente.');
        }

        handleSaveFile(files, index, type, dropZone, cardIndex);
      };

      img.onerror = () => {
        AlertService.toastWarning(
          'Error al cargar la imagen. No se pudo determinar la resolución.'
        );
      };
    }
    if (dropZone === 'headerVideo') {
      if (file.size > 5 * 1024 * 1024) {
        AlertService.toastInfo('¡Atención!, El tamaño máximo permitido para el video es de 5 MB.');
        return;
      }
      if (!isVideo) {
        AlertService.toastInfo('¡Atención!, Solo se permiten cargar archivo .mp4.');
        return;
      }

      handleSaveFile(files, index, type, dropZone, cardIndex);
    }
    if (dropZone === 'headerFile') {
      if (file.size > 5 * 1024 * 1024) {
        AlertService.toastInfo(
          '¡Atención!, El tamaño máximo permitido para tu archivo es de 5 MB.'
        );
        return;
      }
      if (!isFile) {
        AlertService.toastInfo(
          '¡Atención!, El formato de tu archivo debe ser .pdf, .doc, .docx, .txt, .zip, .rar, .pptx, .xls, .xlsx o .csv'
        );
        return;
      }
      handleSaveFile(files, index, type, dropZone, cardIndex);
    }
  };

  const handleResizeImage = (
    file: File,
    targetWidth: number,
    targetHeight: number
  ): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        ctx?.drawImage(img, 0, 0, targetWidth, targetHeight);
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Error resizing image'));
          }
        }, file.type);
      };
      img.onerror = reject;
    });
  };

  const handleSaveFile = (
    files: any[],
    index: number,
    type: string,
    dropZone: string,
    cardIndex: number
  ) => {
    const data = new FormData();
    data.append('file', files[0].file);

    const promise = async () => {
      const response = await AssetsServices.uploadMedia(data);
      setFiles([]);
      if (dropZone === 'posterImage') {
        setValue(`elements.${index}.URLImagePoster`, response.url, { shouldValidate: true });
        if (type !== 'richCard_Carrousel') {
          setValue(`elements.${index}.URLImagePoster`, response.url, { shouldValidate: true });
        } else {
          setValue(`elements.${index}.cards.${cardIndex}.URLImagePoster`, response.url, {
            shouldValidate: true
          });
        }
      } else if (dropZone === 'headerFile') {
        setValue(`elements.${index}.URLFile`, response.url, { shouldValidate: true });
      } else {
        if (type !== 'richCard_Carrousel') {
          setValue(
            watch(`elements.${index}.videoCheck`)
              ? `elements.${index}.URLVideo`
              : `elements.${index}.URLImage`,
            response.url,
            { shouldValidate: true }
          );
        } else {
          setValue(
            watch(`elements.${index}.cards.${cardIndex}.videoCheck`)
              ? `elements.${index}.cards.${cardIndex}.URLVideo`
              : `elements.${index}.cards.${cardIndex}.URLImage`,
            response.url,
            { shouldValidate: true }
          );
        }
      }
    };
    AlertService.toastLoading(
      promise(),
      'Guardando archivo...',
      'El archivo se guardó correctamente',
      'Ocurrió un error al guardar el archivo, intenta nuevamente'
    );
  };

  // RichCard actions

  const handleRemoveAction = (elementIndex: number, actionIndex: number) => {
    const elements = watch('elements') || [];
    const actions = elements[elementIndex]?.actions || [];

    actions.splice(actionIndex, 1);
    setValue(`elements.${elementIndex}.actions`, actions, { shouldValidate: true });
    setTimeout(() => {
      actions.forEach((_, i) => {
        clearErrors([
          `elements.${elementIndex}.actions.${i}.gnlLabel`,
          `elements.${elementIndex}.actions.${i}.linkURL`,
          `elements.${elementIndex}.actions.${i}.phoneNumber`,
          `elements.${elementIndex}.actions.${i}.label`,
          `elements.${elementIndex}.actions.${i}.latitude`,
          `elements.${elementIndex}.actions.${i}.longitude`,
          `elements.${elementIndex}.actions.${i}.startTime`,
          `elements.${elementIndex}.actions.${i}.endTime`,
          `elements.${elementIndex}.actions.${i}.titleC`,
          `elements.${elementIndex}.actions.${i}.descriptionC`
        ]);
      });
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, maxLength: number) => {
    const currentLength = e.currentTarget.value.length;
    if (currentLength >= maxLength && e.key !== 'Backspace' && e.key !== 'Delete') {
      e.preventDefault();
    }
  };

  const handleRemoveElement = (index: number) => {
    const currentElements = [...(watch('elements') || [])];
    currentElements.splice(index, 1);
    setValue('elements', currentElements, { shouldValidate: false });
  };

  const handleActionClick = (
    elementIndex: number,
    cardIndex: number | null,
    action: string,
    type: string
  ) => {
    const elements = watch('elements') || [];
    const element = elements[elementIndex];

    if (!Array.isArray(element.actions)) {
      element.actions = [];
    }

    const actions = element.actions;
    const maxActions = type === 'contentInfo' ? 11 : 4;

    if (actions.length < maxActions) {
      actions.push({
        type: action,
        gnlLabel: '',
        linkURL: '',
        phoneNumber: '',
        label: '',
        latitude: 0,
        longitude: 0,
        startTime: '',
        endTime: '',
        titleC: '',
        descriptionC: ''
      });
      setValue('elements', elements);
      const actionIndex = actions.length - 1;
      clearErrors([
        `elements.${elementIndex}.actions.${actionIndex}.gnlLabel`,
        `elements.${elementIndex}.actions.${actionIndex}.linkURL`,
        `elements.${elementIndex}.actions.${actionIndex}.phoneNumber`,
        `elements.${elementIndex}.actions.${actionIndex}.label`,
        `elements.${elementIndex}.actions.${actionIndex}.latitude`,
        `elements.${elementIndex}.actions.${actionIndex}.longitude`,
        `elements.${elementIndex}.actions.${actionIndex}.startTime`,
        `elements.${elementIndex}.actions.${actionIndex}.endTime`,
        `elements.${elementIndex}.actions.${actionIndex}.titleC`,
        `elements.${elementIndex}.actions.${actionIndex}.descriptionC`
      ]);
    } else {
      AlertService.toastInfo(
        `¡Atención!, Solo puedes agregar hasta ${maxActions} botones por ${type === 'contentInfo' ? 'sección' : 'tarjeta'}.`
      );
    }
  };

  // RichCardCarrousel actions

  const handleAddCardCarrousel = (index: number) => {
    const newCard: Cards = {
      type: 'richCard',
      URLImage: '',
      URLImagePoster: '',
      URLVideo: '',
      title: '',
      description: '',
      gnlLabel: '',
      linkURL: '',
      imageCheck: true,
      videoCheck: false,
      suggestedAction: true
    };
    const currentElements = getValues('elements') || [];

    if (currentElements[index]) {
      if (!currentElements[index].cards) {
        currentElements[index].cards = [];
      }
      currentElements[index]?.cards?.push(newCard);
      setValue('elements', currentElements);
    }
  };

  const handleRemoveCardCarrousel = (elementIndex: number, cardIndex: number) => {
    const currentElements = getValues('elements') || [];
    if (currentElements[elementIndex]) {
      if (!currentElements[elementIndex].cards) {
        currentElements[elementIndex].cards = [];
      }
      if (currentElements[elementIndex]?.cards) {
        currentElements[elementIndex].cards = currentElements[elementIndex].cards?.filter(
          (_: any, idx: number) => idx !== cardIndex
        );
      }
      setValue('elements', currentElements, { shouldValidate: false });
    }
  };

  const handleActionClickCard = (elementIndex: number, cardIndex: number, action: string) => {
    const elements = getValues('elements') || [];
    const cards = elements[elementIndex]?.cards;
    if (!cards || !cards[cardIndex]) return;

    const card = cards[cardIndex];
    if (!Array.isArray(card.actions)) {
      card.actions = [];
    }

    if (card.actions.length < 4) {
      card.actions.push({
        type: action,
        gnlLabel: '',
        linkURL: '',
        phoneNumber: '',
        label: '',
        latitude: 0,
        longitude: 0,
        startTime: '',
        endTime: '',
        titleC: '',
        descriptionC: ''
      });
      setValue(`elements.${elementIndex}.cards.${cardIndex}.actions`, card.actions);
    } else {
      AlertService.toastInfo(
        '¡Atención!, Solo puedes agregar hasta 4 botones por cada tarjeta de tu carrosuel.'
      );
    }

    clearErrors(`elements.${elementIndex}.cards.${cardIndex}.actions`);
  };

  const handleRemoveActionCard = (elementIndex: number, cardIndex: number, actionIndex: number) => {
    const elements = getValues('elements') || [];
    const card = elements[elementIndex]?.cards?.[cardIndex];
    if (!card || !Array.isArray(card.actions)) return;
    card.actions.splice(actionIndex, 1);
    setValue(`elements.${elementIndex}.cards.${cardIndex}.actions`, card.actions);
    clearErrors(`elements.${elementIndex}.cards.${cardIndex}.actions`);
  };

  // Sugestions general actions

  const handleGeneralActionClick = (actionType: string) => {
    const currentSuggestions = getValues('suggestions') || [];

    if (currentSuggestions.length >= 11) {
      AlertService.toastInfo('¡Atención!, Solo puedes agregar hasta 11 acciones generales.');
      return;
    }

    const newSuggestion = {
      type: actionType,
      gnlLabel: '',
      linkURL: '',
      phoneNumber: '',
      label: '',
      latitude: 0,
      longitude: 0,
      startTime: '',
      endTime: '',
      titleC: '',
      descriptionC: ''
    };

    const updatedSuggestions = [...currentSuggestions, newSuggestion];
    const newIndex = updatedSuggestions.length;
    setValue('suggestions', updatedSuggestions, { shouldValidate: false });
    clearErrors([
      `suggestions.${newIndex - 1}.gnlLabel`,
      `suggestions.${newIndex - 1}.linkURL`,
      `suggestions.${newIndex - 1}.phoneNumber`,
      `suggestions.${newIndex - 1}.label`,
      `suggestions.${newIndex - 1}.latitude`,
      `suggestions.${newIndex - 1}.longitude`,
      `suggestions.${newIndex - 1}.startTime`,
      `suggestions.${newIndex - 1}.endTime`,
      `suggestions.${newIndex - 1}.titleC`,
      `suggestions.${newIndex - 1}.descriptionC`
    ]);
  };

  const handleRemoveSuggestion = (index: number) => {
    const currentSuggestions = getValues('suggestions') || [];
    const updatedSuggestions = currentSuggestions.filter((_, i) => i !== index);
    setValue('suggestions', updatedSuggestions, { shouldValidate: true });
    setTimeout(() => {
      updatedSuggestions.forEach((_, i) => {
        clearErrors([
          `suggestions.${i}.gnlLabel`,
          `suggestions.${i}.linkURL`,
          `suggestions.${i}.phoneNumber`,
          `suggestions.${i}.label`,
          `suggestions.${i}.latitude`,
          `suggestions.${i}.longitude`,
          `suggestions.${i}.startTime`,
          `suggestions.${i}.endTime`,
          `suggestions.${i}.titleC`,
          `suggestions.${i}.descriptionC`
        ]);
      });
    }, 0);
  };

  return {
    t,
    register,
    watch,
    setValue,
    clearErrors,
    errors,
    getValues,
    handleRemoveAction,
    handleKeyDown,
    handleRemoveElement,
    handleActionClick,
    files,
    handleUpdateFiles,
    handleAddCardCarrousel,
    handleRemoveCardCarrousel,
    handleActionClickCard,
    handleRemoveActionCard,
    actions,
    handleGeneralActionClick,
    handleRemoveSuggestion
  };
};
